import type { FieldErrors } from 'react-hook-form';

import { match } from 'ts-pattern';

import type { ContactLanguage } from '~/api/clients/types';
import type { Product } from '~/api/products/types';

import { uuidv4 } from '~/utils/string';

import type { DocumentFormEntryLine, DocumentFormSection, DocumentFormTextLine } from '../types';

/**
 * Create a new creative line
 */
export const createLine = () =>
  ({
    uuid: uuidv4(),
    type: 'entry',
    description: '' as const,
    price: 0 as const,
    discountType: null,
    discountValue: null,
    quantity: 1 as const,
    unit: null,
    activityType: 'CreativeGeneral',
    productId: null,
  }) satisfies DocumentFormEntryLine;

/**
 * Create a new line based on a product
 */
export const createLineFromProduct = (product: Product) =>
  ({
    ...createLine(),
    price: product.price ?? 0,
    unit: product.unit,
    activityType: product.activityType,
    productId: product.id,
  }) satisfies DocumentFormEntryLine;

/**
 * Create a new photo album section
 */
export const createPhotoAlbumSection = (shouldCreateLine = true) =>
  ({
    isPhotoAlbum: true,
    lines: shouldCreateLine ? [createLine()] : [],
  }) satisfies DocumentFormSection;

/**
 * Create a text-only line
 */
export const createTextLine = () =>
  ({
    uuid: uuidv4(),
    type: 'text',
    description: '' as const,
  }) satisfies DocumentFormTextLine;

/**
 * Check if a line is free based on the discount options
 */
export const isFreeLine = (line: DocumentFormEntryLine) =>
  match(line.discountType)
    .with('percentage', () => (line.discountValue ?? 0) >= 100)
    .with('flatRate', () => (line.discountValue ?? 0) >= line.price * line.quantity)
    .with(null, () => false)
    .exhaustive();

/**
 * Whether anything React Hook Form *can* focus is invalid.
 *
 * The `sections` root rules are validated by `useFieldArray`, so their error has no input behind it and
 * RHF's own focus-on-error skips it. Every other error does have one, and RHF has already focused the
 * first of them — so the lines error should only pull the page towards itself when it stands alone.
 */
export const hasFocusableError = (errors: FieldErrors) =>
  Object.entries(errors).some(([name, error]) => name !== 'sections' || Object.keys(error ?? {}).some((key) => key !== 'root'));

export const getProductName = (product: Product | null, preferredLanguage: ContactLanguage, userLanguage: ContactLanguage): string =>
  product?.name[preferredLanguage] ?? product?.name[userLanguage] ?? product?.name.nl ?? product?.name.fr ?? product?.name.en ?? '';
